/**
 * Geographic utilities for Ghana map application
 */

import { ALLOW_FIELD_MAPPING_OUTSIDE_GHANA } from '../lib/env';

// Ghana geographic bounds
export const GHANA_BOUNDS: [[number, number], [number, number]] = [
  [-3.3, 4.5],   // Southwest corner [lng, lat]
  [1.3, 11.3]   // Northeast corner [lng, lat]
];

// Accra center point (default location)
export const ACCRA_CENTER = { lng: -0.187, lat: 5.603 };

// Default zoom level (matches MAP_DEFAULT_ZOOM in mapConfig)
export const DEFAULT_ZOOM = 15;

/**
 * Check if coordinates are within Ghana bounds
 */
export const isInGhana = (lng: number, lat: number): boolean => {
  return lng >= GHANA_BOUNDS[0][0] && lng <= GHANA_BOUNDS[1][0] &&
    lat >= GHANA_BOUNDS[0][1] && lat <= GHANA_BOUNDS[1][1];
};

/** True when device GPS may be used for field mapping (trace / contributions). */
export function useDeviceLocationForFieldMapping(lng: number, lat: number): boolean {
  return isInGhana(lng, lat) || ALLOW_FIELD_MAPPING_OUTSIDE_GHANA;
}

/** Resolve map locate target — Accra fallback unless field-mapping bypass is enabled. */
export function resolveFieldMappingLocateTarget(location: { lng: number; lat: number }): {
  lng: number;
  lat: number;
} {
  if (useDeviceLocationForFieldMapping(location.lng, location.lat)) {
    return location;
  }
  return ACCRA_CENTER;
}

/**
 * Ghana bounds used by POST /map/contributions (must match API `_validate_contribution_geometry_in_ghana`).
 */
export function isLngLatInContributionGhanaBounds(lng: number, lat: number): boolean {
  return 4.5 <= lat && lat <= 11.5 && -3.5 <= lng && lng <= 1.5;
}

function collectLngLatPairs(coords: unknown): [number, number][] {
  const out: [number, number][] = [];
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (
      c.length === 2 &&
      typeof c[0] === 'number' &&
      typeof c[1] === 'number' &&
      !Array.isArray(c[0]) &&
      !Array.isArray(c[1])
    ) {
      out.push([c[0], c[1]]);
      return;
    }
    for (const item of c) walk(item);
  };
  walk(coords);
  return out;
}

/** True if every vertex of the geometry lies within API Ghana bounds (for client-side hints / logging). */
export function contributionGeometryInApiGhanaBounds(geometry: {
  type: string;
  coordinates: unknown;
}): boolean {
  const pairs = collectLngLatPairs(geometry.coordinates);
  if (pairs.length === 0) return false;
  return pairs.every(([lng, lat]) => isLngLatInContributionGhanaBounds(lng, lat));
}
